import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import { Moment } from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import DatesPickerDropdown from 'src/components/DatesPickerDropdown/DatesPickerDropdown'
import SvgArrowBack from 'src/components/Svgs/SvgArrowBack'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'

import style from './DateRangePresetMenu.style'

export interface DateRangePresetOption {
  /** Stable identifier passed back to onSelectOption. */
  key: string
  /** Already-translated row label (e.g. "Today", "Last 30 Days", "MTD"). */
  label: string
  /** Already-translated date-range sub-label (e.g. "Jun 1 – Jun 30, 2026"). */
  subLabel: string
  isActive: boolean
  /** Optional data-testid for the row (e2e). */
  testId?: string
}

export interface DateRangePresetCustom {
  /** Already-translated label for the custom row (e.g. "Custom Date Range"). */
  label: string
  isActive: boolean
  /** Sub-label shown on the custom row while it is the active selection. */
  activeSubLabel?: string
  since: Moment | undefined
  until: Moment | undefined
  setSince: (val: Moment | undefined) => void
  setUntil: (val: Moment | undefined) => void
  /**
   * When true, opening the custom view while a preset is active seeds the
   * calendar with the consumer's current range instead of starting blank.
   */
  seedInactiveRange?: boolean
  /** Interpret/emit calendar dates in UTC (defaults to local). */
  utc?: boolean
}

export interface DateRangePresetMenuProps {
  options: DateRangePresetOption[]
  onSelectOption: (key: string) => void
  /** When provided, renders a "Custom Date Range" row + calendar sub-view. */
  custom?: DateRangePresetCustom
  /** Already-translated "Back" label for the custom sub-view. */
  backLabel?: string
  /** Already-translated reset label; renders the reset footer when paired with onReset. */
  resetLabel?: string
  onReset?: () => void
  /**
   * Open state of the parent dropdown. When it transitions from open to closed,
   * the custom calendar sub-view is collapsed back to the preset list.
   */
  parentOpen?: boolean
  /** Optional data-testid for the menu container (e2e). */
  menuTestId?: string
  /** Optional data-testid for the reset button (e2e). */
  resetTestId?: string
}

/**
 * Shared, preset-configurable date-range menu body: a list of preset rows
 * (label + date sub-label), an optional "Custom Date Range" row with a calendar
 * sub-view, and an optional reset footer. The consumer supplies the presets,
 * their resolved date sub-labels, and what each selection does; the menu owns
 * only its preset-list ↔ custom-calendar view state and reads the brand CTA
 * color for the back arrow. Used by both the transactions/accounting filter and
 * the reporting dashboard so the two stay visually identical.
 */
const DateRangePresetMenu: React.FC<DateRangePresetMenuProps> = ({
  options,
  onSelectOption,
  custom,
  backLabel,
  resetLabel,
  onReset,
  parentOpen,
  menuTestId,
  resetTestId,
}) => {
  const brandColorCta = useSelector(selectBrandColorCta)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const prevOpenRef = useRef(parentOpen)

  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = parentOpen
    if (wasOpen && !parentOpen) {
      const timer = setTimeout(() => setShowDatePicker(false), 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [parentOpen])

  const handleOptionClick = (e: React.SyntheticEvent, key: string): void => {
    e.stopPropagation()
    setShowDatePicker(false)
    onSelectOption(key)
  }

  const handleReset = (): void => {
    setShowDatePicker(false)
    onReset?.()
  }

  return (
    <div className="DateRangePresetMenu" css={style}>
      <div
        className={cn('container', {
          large: showDatePicker,
          small: !showDatePicker,
        })}
        data-testid={menuTestId}
      >
        {!showDatePicker && (
          <>
            {options.map((option) => (
              <button
                type="button"
                className={cn('option', { active: option.isActive })}
                key={option.key}
                data-testid={option.testId}
                aria-current={option.isActive ? true : undefined}
                onClick={(e): void => handleOptionClick(e, option.key)}
              >
                <span className="option-type">{option.label}</span>
                <span className="option-value">{option.subLabel}</span>
              </button>
            ))}
            {custom && (
              <button
                type="button"
                className={cn('custom-option', { active: custom.isActive })}
                aria-current={custom.isActive ? true : undefined}
                onClick={(e): void => {
                  // Stop the click from reaching a consumer's outside-click
                  // listener, which would otherwise close the whole dropdown
                  // when this row unmounts on switch to the calendar view.
                  e.stopPropagation()
                  setShowDatePicker(true)
                }}
              >
                {custom.label}
                {custom.isActive && custom.activeSubLabel && (
                  <span className="option-value">{custom.activeSubLabel}</span>
                )}
              </button>
            )}
          </>
        )}
        {showDatePicker && custom && (
          <div>
            <button
              type="button"
              className="back-button"
              onClick={(e): void => {
                e.stopPropagation()
                setShowDatePicker(false)
              }}
            >
              <SvgArrowBack
                width={20}
                height={20}
                viewBox={'0 0 24 24'}
                fill={brandColorCta}
              />
              <span>{backLabel}</span>
            </button>
            <DatesPickerDropdown
              since={
                custom.isActive || custom.seedInactiveRange
                  ? custom.since
                  : undefined
              }
              until={
                custom.isActive || custom.seedInactiveRange
                  ? custom.until
                  : undefined
              }
              setSince={custom.setSince}
              setUntil={custom.setUntil}
              disabled={false}
              startAndKeepOpen={true}
              hideLabel={true}
              hideShadow={true}
              utc={custom.utc}
            />
          </div>
        )}
        {resetLabel && onReset && (
          <div className={'reset-container'}>
            <Button
              onClick={handleReset}
              className={'reset-button'}
              variant={'no-border'}
              testId={resetTestId}
            >
              {resetLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DateRangePresetMenu
